import { memo, useEffect, useState } from 'react';

import { Button, Flex, Group, ScrollArea } from '@mantine/core';
import { useForm } from '@mantine/form';

import { IRenter, TRenterRequestType } from '@/shared/types/renter.types';
import { LabelInput } from '@/shared/uikit/components/input';
import classes from '@/shared/uikit/components/input/LabelInput/LabelInput.module.css';
import { ModalSheet } from '@/shared/uikit/components/modalSheet';

const RENTER_NAME_MAX_LENGTH = 255;

type TRenterFormValues = {
	renter_name: string;
};

type RenterFormProps = {
	isOpen: boolean;
	renter: IRenter | null;
	onCreate: (body: TRenterRequestType) => Promise<void>;
	onUpdate: (body: TRenterRequestType, id: number) => Promise<void>;
	handleClose: () => void;
};

export const RenterForm = memo(function RenterForm({
	isOpen,
	renter,
	onCreate,
	onUpdate,
	handleClose,
}: RenterFormProps) {
	const [isSubmitting, setIsSubmitting] = useState(false);

	const form = useForm<TRenterFormValues>({
		initialValues: {
			renter_name: renter?.renter_name ?? '',
		},
		validate: {
			renter_name: (value) => {
				const trimmed = value.trim();

				if (!trimmed) {
					return 'Введите имя арендатора';
				}

				if (trimmed.length > RENTER_NAME_MAX_LENGTH) {
					return `Имя не должно превышать ${RENTER_NAME_MAX_LENGTH} символов`;
				}

				return null;
			},
		},
	});

	const handleSubmit = async (data: TRenterFormValues) => {
		const body = { renter_name: data.renter_name.trim() };

		setIsSubmitting(true);

		try {
			if (renter) {
				await onUpdate(body, renter.id);
				return;
			}

			await onCreate(body);
		} catch {
			return;
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleModalClose = () => {
		handleClose();
		form.reset();
	};

	useEffect(() => {
		form.setValues({
			renter_name: renter?.renter_name ?? '',
		});
		form.resetDirty();
		form.clearErrors();
	}, [renter, isOpen]);

	return (
		<ModalSheet
			title={`${renter ? 'Изменение' : 'Создание'} арендатора`}
			opened={isOpen}
			onClose={handleModalClose}
			size="lg"
			scrollAreaComponent={ScrollArea.Autosize}
		>
			<form
				onSubmit={form.onSubmit((values) => {
					void handleSubmit(values);
				})}
			>
				<Flex direction="column" gap="sm">
					<LabelInput
						classNames={classes}
						label="Имя"
						placeholder="Введите имя арендатора"
						withAsterisk
						maxLength={RENTER_NAME_MAX_LENGTH}
						{...form.getInputProps('renter_name')}
					/>
					<Group gap="sm" mt="lg">
						<Button type="submit" size="md" loading={isSubmitting}>
							Сохранить
						</Button>
						<Button variant="outline" size="md" onClick={handleModalClose}>
							Отмена
						</Button>
					</Group>
				</Flex>
			</form>
		</ModalSheet>
	);
});
